import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { 
  Bot, Send, User, Loader2, Sparkles, Building2, FolderOpen, 
  Calendar, Users, AlertCircle, TrendingUp, HelpCircle
} from 'lucide-react';
import { apiErrorMessage } from '../lib/apiError';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const SUGGESTED_QUERIES = [
  "How many projects are active?",
  "Which projects are delayed?",
  "Show me all schedules",
  "Which activities are critical?",
  "What is the overall progress?",
  "List all users and their roles"
];

const AIAssistantPage = () => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId] = useState(() => `session-${Date.now()}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Handle sending a query
  const handleSendQuery = async (queryText = query) => {
    if (!queryText.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: queryText.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setQuery('');
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/ai-assistant/org-query', {
        query: queryText.trim(),
        session_id: sessionId
      });

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: response.data.answer,
        dataSummary: response.data.data_summary,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('AI query failed:', err);
      setError(apiErrorMessage(err, 'Failed to get response from AI Assistant'));
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: apiErrorMessage(err, 'Sorry, I encountered an error processing your request. Please try again.'),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  // Handle keyboard submit
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendQuery();
    }
  };

  // Handle suggested query click
  const handleSuggestedQuery = (suggestedQuery) => {
    handleSendQuery(suggestedQuery);
  };

  // Format message content with markdown-like styling
  const formatContent = (content) => {
    if (!content) return null;

    // Split by lines and process
    const lines = content.split('\n');
    const elements = [];
    let currentList = [];
    let listKey = 0;

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${listKey++}`} className="list-disc list-inside space-y-1 my-2">
            {currentList.map((item, i) => (
              <li key={i} className="text-sm text-slate-700">{item}</li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      // Check for bullet points
      if (trimmed.startsWith('- ') || trimmed.startsWith('• ') || trimmed.match(/^\d+\./)) {
        currentList.push(trimmed.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, ''));
      } else {
        flushList();
        if (trimmed) {
          // Check for headers (bold text)
          if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
            elements.push(
              <p key={index} className="font-semibold text-slate-900 mt-3 mb-1">
                {trimmed.replace(/\*\*/g, '')}
              </p>
            );
          } else {
            elements.push(
              <p key={index} className="text-sm text-slate-700 my-1">{trimmed}</p>
            );
          }
        } else if (elements.length > 0) {
          elements.push(<div key={index} className="h-2" />);
        }
      }
    });

    flushList();
    return elements;
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="ai-assistant-page">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Bot className="h-6 w-6 text-blue-600" />
            AI Assistant
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Ask questions about projects, schedules, and activities across your organization
          </p>
        </div>
        <Badge variant="outline" className="text-xs gap-1 shrink-0 self-start sm:self-auto">
          <Sparkles className="h-3 w-3" />
          Powered by Claude
        </Badge>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-4 min-h-0">
        {/* Main Chat Area */}
        <Card className="lg:col-span-3 flex flex-col min-h-0 border-slate-200">
          <CardHeader className="pb-2 border-b border-slate-100 flex-shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-slate-700">Conversation</CardTitle>
              {messages.length > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs h-7 text-slate-400 hover:text-slate-600"
                  onClick={() => setMessages([])}
                >
                  Clear Chat
                </Button>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="flex-1 flex flex-col p-0 min-h-0">
            {/* Messages Area */}
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                    <Bot className="h-8 w-8 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    How can I help you today?
                  </h3>
                  <p className="text-sm text-slate-500 max-w-md mb-6">
                    I can answer questions about your projects, schedules, activities, and organization metrics. 
                    I'm read-only and can only provide analysis - I cannot modify any data.
                  </p>
                  
                  {/* Suggested Queries */}
                  <div className="flex flex-wrap gap-2 justify-center max-w-xs sm:max-w-lg">
                    {SUGGESTED_QUERIES.map((sq, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        className="text-xs h-8 text-slate-600 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
                        onClick={() => handleSuggestedQuery(sq)}
                      >
                        {sq}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {message.type !== 'user' && (
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          message.type === 'error' ? 'bg-red-100' : 'bg-blue-100'
                        }`}>
                          {message.type === 'error' ? (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          ) : (
                            <Bot className="h-4 w-4 text-blue-600" />
                          )}
                        </div>
                      )}
                      
                      <div className={`max-w-[80%] rounded-lg px-4 py-3 ${
                        message.type === 'user' 
                          ? 'bg-blue-600 text-white' 
                          : message.type === 'error'
                            ? 'bg-red-50 border border-red-200'
                            : 'bg-slate-100'
                      }`}>
                        {message.type === 'user' ? (
                          <p className="text-sm">{message.content}</p>
                        ) : (
                          <div className="prose prose-sm max-w-none">
                            {formatContent(message.content)}
                          </div>
                        )}
                        <p className={`text-[10px] mt-2 ${
                          message.type === 'user' ? 'text-blue-200' : 'text-slate-400'
                        }`}>
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                      
                      {message.type === 'user' && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                          <User className="h-4 w-4 text-slate-600" />
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {loading && (
                    <div className="flex gap-3 justify-start">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <Bot className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="bg-slate-100 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Analyzing...
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
            
            {/* Input Area */}
            <div className="p-4 border-t border-slate-100 flex-shrink-0">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  placeholder="Ask about projects, schedules, activities..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  className="flex-1"
                  data-testid="ai-query-input"
                />
                <Button
                  onClick={() => handleSendQuery()}
                  disabled={!query.trim() || loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                  data-testid="ai-send-button"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  Send
                </Button>
              </div>
              {error && (
                <p className="text-xs text-red-500 mt-2">{error}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sidebar - Capabilities */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              What I Can Help With
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-2 rounded-lg bg-slate-50">
                <Building2 className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-slate-700">Organization Overview</p>
                  <p className="text-[11px] text-slate-500">Project counts, status summary, metrics</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-2 rounded-lg bg-slate-50">
                <FolderOpen className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-slate-700">Project Analysis</p>
                  <p className="text-[11px] text-slate-500">Status, progress, delays, completion</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-2 rounded-lg bg-slate-50">
                <Calendar className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-slate-700">Schedule Status</p>
                  <p className="text-[11px] text-slate-500">Activities, critical path, variances</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-2 rounded-lg bg-slate-50">
                <Users className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-slate-700">User Information</p>
                  <p className="text-[11px] text-slate-500">Team members, roles, assignments</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-2 rounded-lg bg-slate-50">
                <TrendingUp className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-slate-700">Progress Tracking</p>
                  <p className="text-[11px] text-slate-500">Completion rates, trends, performance</p>
                </div>
              </div>
            </div>
            
            <div className="pt-3 border-t border-slate-100">
              <p className="text-[10px] text-slate-400 uppercase font-medium mb-2">Limitations</p>
              <ul className="space-y-1">
                <li className="text-[11px] text-slate-500 flex items-center gap-1">
                  <span className="text-red-400">•</span> Cannot modify data
                </li>
                <li className="text-[11px] text-slate-500 flex items-center gap-1">
                  <span className="text-red-400">•</span> Cannot run simulations
                </li>
                <li className="text-[11px] text-slate-500 flex items-center gap-1">
                  <span className="text-red-400">•</span> Cannot access sensitive info
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIAssistantPage;
